import React, { useEffect, useState } from 'react';
import { Sliders, Loader2, Save, RefreshCcw, AlertTriangle, Trash2, BellOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const FIELDS = [
  {
    key: 'diff_vp_super',
    label: 'Diferencial V-Power ↔ Súper',
    hint: 'V-Power = Súper + este valor. Default Q0.50.',
    formula: 'V-Power = Súper + Δ',
  },
  {
    key: 'diff_super_regular',
    label: 'Diferencial Súper ↔ Regular',
    hint: 'Regular = Súper − este valor. Default Q1.00.',
    formula: 'Regular = Súper − Δ',
  },
  {
    key: 'diff_diesel_ulsd',
    label: 'Diferencial Diesel ↔ ULSD',
    hint: 'ULSD = Diesel + este valor. Default Q1.30.',
    formula: 'ULSD = Diesel + Δ',
  },
  {
    key: 'price_alert_threshold',
    label: 'Umbral de alerta de precio (markers)',
    hint: 'Notifica a los admins por CADA marker que cambie un precio en ≥ este valor. En 0 = desactivado (recomendado): solo llega la notificación consolidada al concluir el tracking (AD-P04).',
    formula: '|nuevo − anterior| ≥ Δ → alerta',
  },
  {
    key: 'tracking_diff_min',
    label: 'Diferencia mínima para ventana de tracking',
    hint: 'Las diferencias ≤ este valor NO generan ventana comparativa ni alerta (reduce ruido). Default Q0.02 · pon 0 para alertar cualquier diferencia. Solo Autoservicio genera alertas; Full queda en el récord.',
    formula: '|marker − propio| > Δ → ventana',
  },
  {
    key: 'tracking_daily_target',
    label: 'Meta de trackings diarios por estación',
    hint: 'Rondas de tracking COMPLETADAS que cada estación debe cumplir al día. Alimenta los semáforos del Histórico de TMs (MO-P07) y Cumplimiento tracking (AD-P19). Default 3 · entre 1 y 10.',
    formula: 'rondas completadas ≥ meta → 🟢',
    integer: true,
  },
];

const AdminParameters = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useState({});
  const [draft, setDraft] = useState({});
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState('');
  const [resetting, setResetting] = useState(false);
  const { logout } = useAuth();
  const QUIET_KEYS = ['quiet_hours_enabled', 'quiet_hours_start', 'quiet_hours_end'];
  const dirty = FIELDS.some(f => String(draft[f.key] ?? '') !== String(params[f.key] ?? ''))
    || QUIET_KEYS.some(k => String(draft[k] ?? '') !== String(params[k] ?? ''));

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/parameters');
      setParams(r.data);
      setDraft({
        diff_vp_super: r.data.diff_vp_super,
        diff_super_regular: r.data.diff_super_regular,
        diff_diesel_ulsd: r.data.diff_diesel_ulsd,
        price_alert_threshold: r.data.price_alert_threshold,
        tracking_diff_min: r.data.tracking_diff_min ?? 0.02,
        tracking_daily_target: r.data.tracking_daily_target ?? 3,
        quiet_hours_enabled: !!r.data.quiet_hours_enabled,
        quiet_hours_start: r.data.quiet_hours_start || '22:00',
        quiet_hours_end: r.data.quiet_hours_end || '06:00',
      });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(f => {
        const val = f.integer ? parseInt(draft[f.key], 10) : parseFloat(draft[f.key]);
        if (!Number.isNaN(val)) payload[f.key] = val;
      });
      payload.quiet_hours_enabled = !!draft.quiet_hours_enabled;
      if (/^\d{2}:\d{2}$/.test(draft.quiet_hours_start || '')) payload.quiet_hours_start = draft.quiet_hours_start;
      if (/^\d{2}:\d{2}$/.test(draft.quiet_hours_end || '')) payload.quiet_hours_end = draft.quiet_hours_end;
      const r = await api.put('/parameters', payload);
      setParams(r.data);
      toast.success('Parámetros guardados');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const performReset = async () => {
    if (resetText !== 'RESET') {
      toast.error("Debes escribir 'RESET' (en mayúsculas) para confirmar");
      return;
    }
    setResetting(true);
    try {
      const r = await api.post('/admin/factory-reset', { confirm: 'RESET' });
      const totalWiped = Object.values(r.data?.wiped || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Reset completado · ${totalWiped} registros eliminados. Volviendo a iniciar sesión…`);
      setResetOpen(false);
      // Force a logout so the session reflects the wiped state.
      setTimeout(async () => {
        try { await logout(); } catch (_) { /* ignore */ }
        window.location.href = '/login';
      }, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo ejecutar el reset');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-parameters-page">
      <div>
        <h2 className="font-serif text-[36px] leading-tight tracking-tight">Parámetros</h2>
        <p className="text-[13px] text-[#5a5a5a] mt-1">
          Diferenciales de precios aplicados al sugerir precios al aprobar una solicitud de tracking en AD-P04.
          Súper y Diesel se igualan al precio más bajo de la competencia; los demás se calculan en base a éstos.
        </p>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
      ) : (
        <div className="bg-white border border-[#e8e5de] rounded-xl divide-y divide-[#e8e5de]">
          {FIELDS.map(f => (
            <div key={f.key} className="p-5 flex flex-col md:flex-row md:items-center md:gap-6">
              <div className="flex-1 mb-3 md:mb-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <Sliders size={13} className="text-[#8a8579]" strokeWidth={1.6}/>
                  <Label htmlFor={`p-${f.key}`} className="text-[13.5px] font-medium">{f.label}</Label>
                </div>
                <p className="text-[12px] text-[#5a5a5a]">{f.hint}</p>
                <p className="text-[11px] text-[#8a8579] mt-1 font-mono">{f.formula}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[12px] text-[#8a8579]">{f.integer ? '#' : 'Q'}</span>
                <Input
                  id={`p-${f.key}`}
                  type="number"
                  step={f.integer ? '1' : '0.01'}
                  min={f.integer ? 1 : undefined}
                  max={f.integer ? 10 : undefined}
                  value={draft[f.key] ?? ''}
                  onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                  className="w-28 h-10 bg-[#f8f6f1] text-[14px] tabular-nums text-right"
                  data-testid={`param-${f.key}`}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && (
        <div className="bg-white border border-[#e8e5de] rounded-xl" data-testid="quiet-hours-card">
          <div className="p-5 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <BellOff size={13} className="text-[#8a8579]" strokeWidth={1.6}/>
                <Label className="text-[13.5px] font-medium">Horario silencioso (notificaciones push)</Label>
              </div>
              <p className="text-[12px] text-[#5a5a5a]">
                Durante este horario no se envían notificaciones push al equipo.
                Las notificaciones siguen quedando registradas en la campanita para revisarlas después.
              </p>
            </div>
            <Switch
              checked={!!draft.quiet_hours_enabled}
              onCheckedChange={v => setDraft(d => ({ ...d, quiet_hours_enabled: v }))}
              data-testid="quiet-hours-toggle"
            />
          </div>
          {draft.quiet_hours_enabled && (
            <div className="px-5 pb-5 flex flex-wrap items-end gap-4">
              <div>
                <Label htmlFor="qh-start" className="text-[11px] uppercase tracking-wider text-[#8a8579]">Desde</Label>
                <Input
                  id="qh-start"
                  type="time"
                  value={draft.quiet_hours_start || '22:00'}
                  onChange={e => setDraft(d => ({ ...d, quiet_hours_start: e.target.value }))}
                  className="w-32 h-10 bg-[#f8f6f1] mt-1 tabular-nums"
                  data-testid="quiet-hours-start"
                />
              </div>
              <div>
                <Label htmlFor="qh-end" className="text-[11px] uppercase tracking-wider text-[#8a8579]">Hasta</Label>
                <Input
                  id="qh-end"
                  type="time"
                  value={draft.quiet_hours_end || '06:00'}
                  onChange={e => setDraft(d => ({ ...d, quiet_hours_end: e.target.value }))}
                  className="w-32 h-10 bg-[#f8f6f1] mt-1 tabular-nums"
                  data-testid="quiet-hours-end"
                />
              </div>
              <p className="text-[11.5px] text-[#8a8579] pb-2">
                Hora de Guatemala · si "Desde" es mayor que "Hasta", el horario cruza la medianoche (ej. 22:00 → 06:00).
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="h-10 rounded-full bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black text-[12.5px] px-5"
          data-testid="save-parameters"
        >
          {saving ? <Loader2 size={13} className="mr-1.5 animate-spin"/> : <Save size={13} className="mr-1.5"/>}
          Guardar cambios
        </Button>
        {dirty && (
          <Button
            type="button"
            variant="outline"
            onClick={load}
            className="h-10 rounded-full text-[12.5px]"
          >
            <RefreshCcw size={12} className="mr-1.5"/> Descartar
          </Button>
        )}
        {params.updated_at && (
          <span className="text-[11px] text-[#8a8579] ml-auto">Última actualización: {new Date(params.updated_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}</span>
        )}
      </div>

      <div className="bg-[#f8f6f1] border border-[#e8e5de] rounded-xl p-5">
        <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579] mb-2">Ejemplo del cálculo</p>
        <p className="text-[12.5px] text-[#3a3a3a] leading-relaxed">
          Si el competidor más barato tiene <strong>Súper Q29.99</strong> y <strong>Diesel Q30.00</strong>,
          se sugerirá:
        </p>
        <ul className="text-[12.5px] text-[#3a3a3a] mt-2 space-y-0.5 font-mono">
          <li>V-Power = 29.99 + {Number(draft.diff_vp_super || 0).toFixed(2)} = <strong>Q{(29.99 + Number(draft.diff_vp_super || 0)).toFixed(2)}</strong></li>
          <li>Súper = <strong>Q29.99</strong></li>
          <li>Regular = 29.99 − {Number(draft.diff_super_regular || 0).toFixed(2)} = <strong>Q{(29.99 - Number(draft.diff_super_regular || 0)).toFixed(2)}</strong></li>
          <li>Diesel = <strong>Q30.00</strong></li>
          <li>ULSD = 30.00 + {Number(draft.diff_diesel_ulsd || 0).toFixed(2)} = <strong>Q{(30.00 + Number(draft.diff_diesel_ulsd || 0)).toFixed(2)}</strong></li>
        </ul>
      </div>

      {/* DANGER ZONE */}
      <div className="bg-white border border-[#fecaca] rounded-xl overflow-hidden" data-testid="danger-zone-card">
        <div className="px-5 py-3 border-b border-[#fecaca] bg-[#fef2f2] flex items-center gap-2">
          <AlertTriangle size={14} className="text-[#991b1b]"/>
          <h3 className="text-[13px] font-medium text-[#991b1b]">Zona de peligro</h3>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <p className="text-[13px] font-medium text-[#1c1c1c]">Reiniciar la aplicación a estado limpio</p>
            <p className="text-[12px] text-[#5a5a5a] mt-1 leading-relaxed">
              Borra <strong>todas</strong> las estaciones, zonas, capturas, precios, notificaciones, push,
              auditoría, marcas, mapeos, pricing por estación, parámetros y usuarios — excepto el
              administrador protegido <span className="font-mono bg-[#f3f0e9] px-1 py-0.5 rounded">marivera@uno-ca.com</span>.
              Los 22 departamentos de Guatemala se vuelven a sembrar automáticamente.
            </p>
            <p className="text-[11.5px] text-[#991b1b] mt-2">⚠ Esta acción no se puede deshacer.</p>
          </div>
          <Button
            type="button"
            onClick={() => { setResetText(''); setResetOpen(true); }}
            className="h-10 rounded-full bg-[#991b1b] hover:bg-[#7c1d2e] text-white text-[12.5px]"
            data-testid="open-factory-reset"
          >
            <Trash2 size={13} className="mr-1.5"/> Reiniciar aplicación…
          </Button>
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={(o) => { if (!resetting) setResetOpen(o); if (!o) setResetText(''); }}>
        <DialogContent className="max-w-md bg-[#f8f6f1]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#991b1b] flex items-center gap-2">
              <AlertTriangle size={20}/> Confirmar reinicio total
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[12.5px] text-[#3a3a3a]">
            <p>
              Esto eliminará <strong>todos los datos operativos</strong> de la aplicación:
              estaciones, zonas, capturas, precios oficiales, notificaciones, push, audit log,
              marcas, mapeos, pricing por estación, parámetros y usuarios (excepto el admin protegido).
            </p>
            <p className="text-[#991b1b] font-medium">No se puede deshacer.</p>
            <div className="bg-white border border-[#fecaca] rounded-md p-3">
              <Label className="text-[11.5px] uppercase tracking-wider text-[#991b1b]">
                Escribe RESET (en mayúsculas) para confirmar
              </Label>
              <Input
                value={resetText}
                onChange={e => setResetText(e.target.value)}
                placeholder="RESET"
                className="mt-1 bg-white font-mono"
                autoFocus
                autoComplete="off"
                data-testid="factory-reset-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setResetOpen(false)}
              disabled={resetting}
              data-testid="factory-reset-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={performReset}
              disabled={resetText !== 'RESET' || resetting}
              className="bg-[#991b1b] hover:bg-[#7c1d2e] text-white"
              data-testid="factory-reset-confirm"
            >
              {resetting ? (
                <><Loader2 size={13} className="mr-1.5 animate-spin"/> Reiniciando…</>
              ) : (
                <><Trash2 size={13} className="mr-1.5"/> Reiniciar todo</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminParameters;
